import { useState, useEffect } from "react";
import InputElementComponent from "../../common/InputElementComponent";
import { inputElementModel } from "../../../models/common/inputElement.model";
import { buttonElementModel } from "../../../models/common/buttonElement.model";
import ButtonElementComponent from "../../common/ButtonElementComponent";
import { useParams, useSearchParams } from "react-router-dom";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { transactionClientActions } from "../../../store/actions/transactions/transactionClient.actions";
import validationHelper from "../../../utils/helpers/validation.helper";
import queryParamsHelper from "../../../utils/helpers/queryParams.helper";
import driverSessionService from "../../../services/storage/driverSession.service";
import driverSecurityErrorEnum from "../../../enums/errors/transactions/driverSecurity/driverSecurityError.enum";

export const ContactValidationStepComponent = ({ transactionClientActions, handleNext, handleReset }) => {
  const { companyId, chargePointId } = useParams();
  const [searchParams] = useSearchParams();
  
  const clientId = queryParamsHelper.get(searchParams, 'clientId');

  const [accessCode, setAccessCode] = useState("");
  const [error, setError] = useState("");
  const [countdown, setCountdown] = useState(60); // To handle countdown starting at 60 seconds
  const [isButtonDisabled, setIsButtonDisabled] = useState(true); // To disable the resend button initially

  // Start the countdown on component load or when resend is triggered
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000); // Decrease the countdown every second
      return () => clearTimeout(timer); // Clean up the timer
    } else {
      setIsButtonDisabled(false); // Enable the button when countdown finishes
    }
  }, [countdown]);

  // Reset the countdown when the component loads or after resending the code
  useEffect(() => {
    setCountdown(60); // Start with 60 seconds countdown
  }, []);

  // Function to handle input change
  const handleInputChange = (event) => {
    const { value } = event.target;
    setAccessCode(value);
  };

  // Function to validate the input
  const validate = () => {
    let errorMessage;
    if (!accessCode) errorMessage = "קוד אימות הוא שדה חובה";
    setError(errorMessage);
    return errorMessage ? false : true;
  };

  // Function to handle form submission
  const handleSubmit = async () => {
    // Validate clientId exists before making API call
    if (!clientId) {
      setError("שגיאה: מזהה לקוח לא נמצא. אנא חזור לשלב הקודם ונסה שנית");
      return;
    }

    if (validate()) {
      const response = await transactionClientActions.validateTransactionClientAccessCode(clientId, accessCode, companyId);
      if (validationHelper.validateHttpResponse(response) && response.payload?.driverSession) {
        // Spec 000306 - the proof every card, payment-page and start call now requires.
        driverSessionService.set(response.payload.driverSession);
        handleNext();
      } else {
        setError(getValidateErrorMessage(response));
      }
    }
  };

  // Spec 000306 - the server burns a code after 5 wrong tries, or once it was used.
  const getValidateErrorMessage = (response) => {
    switch (response?.errorCode) {
      case driverSecurityErrorEnum.CodeMismatch: {
        const attemptsLeft = response.payload?.attemptsLeft;
        return attemptsLeft ? `קוד אימות שגוי. נותרו ${attemptsLeft} ניסיונות` : "קוד אימות שגוי! אנה נסה שנית";
      }
      case driverSecurityErrorEnum.CodeBurned:
        return "הקוד ננעל אחרי 5 ניסיונות שגויים. יש לשלוח קוד חדש";
      case driverSecurityErrorEnum.CodeExpired:
        return "תוקף הקוד פג או שכבר נעשה בו שימוש. יש לשלוח קוד חדש";
      default:
        return "קוד אימות שגוי! אנה נסה שנית";
    }
  };

  const getResendErrorMessage = (response) => {
    switch (response?.errorCode) {
      case driverSecurityErrorEnum.ResendTooSoon:
        return "ניתן לשלוח קוד חדש פעם בדקה. נסו שוב בעוד רגע";
      case driverSecurityErrorEnum.ResendLimit:
        return "הגעתם למספר השליחות המרבי. נסו שוב בעוד שעה";
      default:
        return "שגיאה בשליחת קוד אימות. נסה שנית מאוחר יותר";
    }
  };

  // Function to handle resend access code
  const handleResendAccessCode = async () => {
    // Validate both companyId and clientId exist before making API call
    if (!companyId || !clientId) {
      setError("שגיאה: פרטי לקוח לא נמצאו. אנא חזור לשלב הקודם ונסה שנית");
      return;
    }

    const response = await transactionClientActions.resendClientAccessCode(companyId, clientId);
    if (!validationHelper.validateHttpResponse(response)) {
      setError(getResendErrorMessage(response));
      return;
    }
    setError("");
    // Restart the countdown after resending the code
    setCountdown(60);
    setIsButtonDisabled(true); // Disable the button again
  };

  // Generate the access code input element
  const getAccessCodeInputElement = () => {
    const inputElementProps = {
      ...inputElementModel,
      name: "accessCode",
      value: accessCode,
      onChange: handleInputChange,
      label: "קוד אימות",
      placeholder: "קוד אימות",
      required: true,
      error: error,
    };

    return <InputElementComponent inputElementProps={inputElementProps} />;
  };

  // Generate the resend access code button element
  const getResendAccessCodeButtonElement = () => {
    const buttonElementProps = {
      ...buttonElementModel,
      label: isButtonDisabled ? `שלח קוד חדש (${countdown})` : "לשליחת קוד חדש",
      onClick: handleResendAccessCode,
      disabled: isButtonDisabled, // Disable the button during countdown
    };

    return <ButtonElementComponent buttonElementProps={buttonElementProps} />;
  };

  // Generate the submit button element
  const getSubmitButtonElement = () => {
    const buttonElementProps = {
      ...buttonElementModel,
      label: "שמור והמשך",
      onClick: handleSubmit,
    };

    return <ButtonElementComponent buttonElementProps={buttonElementProps} />;
  };

  // Generate the cancel button element
  const getCancelButtonElement = () => {
    const buttonElementProps = {
      ...buttonElementModel,
      label: "בטל",
      onClick: handleReset,
    };

    return <ButtonElementComponent buttonElementProps={buttonElementProps} />;
  };

  return (
    <div className="flexColumn gap20">
      {getAccessCodeInputElement()}
      {getSubmitButtonElement()}
      {getCancelButtonElement()}
      {getResendAccessCodeButtonElement()}
    </div>
  );
};

const mapStateToProps = (state) => ({
  transactionClientReducer: state.transactionClientReducer,
});

const mapDispatchToProps = (dispatch) => {
  return {
    transactionClientActions: bindActionCreators(transactionClientActions, dispatch),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(ContactValidationStepComponent);
